import type {RefObject} from 'react';
import {useRef} from 'react';
import {useIsomorphicLayoutEffect} from '@sail/engine';
import {useStableCallback} from '@sail/react';
import {getOwnerWindow} from '../utils/getOwnerWindow';

type Config = {
  node:
    | HTMLElement
    | (Window & typeof global)
    | RefObject<HTMLElement>
    | null
    | undefined;
  handler: (entry: ResizeObserverEntry) => void;
  avoidInitialEvent?: boolean;
};

const callbacks = new WeakMap<Element, Set<Config['handler']>>();

const getResizeObserver = (() => {
  let resizeObserver: ResizeObserver;
  return () => {
    if (resizeObserver === undefined) {
      resizeObserver = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const elementCallbacks = callbacks.get(entry.target);
          elementCallbacks?.forEach((callback) => callback(entry));
        });
      });
    }

    return resizeObserver;
  };
})();

export function useResizeObserver({
  node: nodeOrRef,
  handler,
  avoidInitialEvent = false,
}: Config): void {
  const hasCalled = useRef(false);

  const stableHandler = useStableCallback(handler);

  useIsomorphicLayoutEffect(() => {
    let node: HTMLElement | (Window & typeof global) | null = null;
    if (nodeOrRef) {
      node = 'current' in nodeOrRef ? nodeOrRef.current : nodeOrRef;
    }

    // We can't use `ResizeObserver` on window, but for
    // consistency we polyfill it with a similar API
    if (node === window) {
      const func = () => {
        // TODO: We need to construct a similar thing to
        // ResizeObserverEntry
        stableHandler({
          borderBoxSize: [],
          contentBoxSize: [],
          contentRect: {
            // eslint-disable-next-line @sail/ui-no-reflow
            width: window.innerWidth,
            // eslint-disable-next-line @sail/ui-no-reflow
            height: window.innerHeight,
          } as DOMRectReadOnly,
          devicePixelContentBoxSize: [],
          target: document.body,
        });
      };

      if (!avoidInitialEvent) {
        func();
      }
      window.addEventListener('resize', func);
      return () => window.removeEventListener('resize', func);
    } else if (node instanceof getOwnerWindow(node).Element) {
      const resizeObserver = getResizeObserver();

      if (!callbacks.has(node)) {
        callbacks.set(node, new Set());
      }

      const elementCallbacks = callbacks.get(node);

      const callback = (entry: ResizeObserverEntry) => {
        if (!avoidInitialEvent || hasCalled.current) {
          stableHandler(entry);
        }
        hasCalled.current = true;
      };

      if (elementCallbacks?.size === 0) {
        resizeObserver.observe(node, {box: 'border-box'});
      }

      elementCallbacks?.add(callback);

      return () => {
        hasCalled.current = false;
        elementCallbacks?.delete(callback);
        if (node instanceof Element && elementCallbacks?.size === 0) {
          resizeObserver.unobserve(node);
        }
      };
    } else if (node !== null) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('useResizeObserver: unrecognized type for node', node);
      }
    }
  }, [nodeOrRef, stableHandler, avoidInitialEvent]);
}
